import React, { useState } from 'react';
import { 
  Bell, 
  Clock, 
  MessageSquare, 
  Smartphone,
  Volume2,
  Save
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';

export default function ReminderSettings() {
  const [saving, setSaving] = useState(false);
  const [frequency, setFrequency] = useState('standard');
  const [tone, setTone] = useState('gentle');
  const [channel, setChannel] = useState('push');
  const [morningTime, setMorningTime] = useState('08:00');
  const [eveningTime, setEveningTime] = useState('20:00');

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      toast.success('Reminder settings saved!');
    }, 1000);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Reminder Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Customize how and when we remind you</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
      
      {/* Notification Channel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notification Channel
          </CardTitle>
          <CardDescription>How would you like to receive reminders?</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={channel} onValueChange={setChannel} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
              channel === 'push' ? 'border-primary bg-primary-light/30' : 'border-border hover:bg-muted/50'
            }`}>
              <RadioGroupItem value="push" id="push" />
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-lg bg-primary-light flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <Label htmlFor="push" className="font-medium cursor-pointer">Push Notifications</Label>
                  <p className="text-xs text-muted-foreground">Receive on your phone</p>
                </div>
              </div>
            </div>
            
            <div className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
              channel === 'whatsapp' ? 'border-primary bg-primary-light/30' : 'border-border hover:bg-muted/50'
            }`}>
              <RadioGroupItem value="whatsapp" id="whatsapp" />
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-lg bg-success-light flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-success" />
                </div>
                <div>
                  <Label htmlFor="whatsapp" className="font-medium cursor-pointer">WhatsApp</Label>
                  <p className="text-xs text-muted-foreground">Messages to your WhatsApp</p>
                </div>
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>
      
      {/* Preferred Times */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Preferred Time Windows
          </CardTitle>
          <CardDescription>When should we send you reminders?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <Label className="mb-2 block">Morning Reminder</Label>
              <Select value={morningTime} onValueChange={setMorningTime}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="06:00">6:00 AM</SelectItem>
                  <SelectItem value="07:00">7:00 AM</SelectItem>
                  <SelectItem value="08:00">8:00 AM</SelectItem>
                  <SelectItem value="09:00">9:00 AM</SelectItem>
                  <SelectItem value="10:00">10:00 AM</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="mb-2 block">Evening Reminder</Label>
              <Select value={eveningTime} onValueChange={setEveningTime}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="18:00">6:00 PM</SelectItem>
                  <SelectItem value="19:00">7:00 PM</SelectItem>
                  <SelectItem value="20:00">8:00 PM</SelectItem>
                  <SelectItem value="21:00">9:00 PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <Label>Session day reminders</Label>
              <p className="text-sm text-muted-foreground">Get reminded on days with scheduled sessions</p>
            </div>
            <Switch defaultChecked />
          </div>
        </CardContent>
      </Card>
      
      {/* Frequency */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Reminder Frequency</CardTitle>
          <CardDescription>How often should we check in with you?</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={frequency} onValueChange={setFrequency} className="space-y-3">
            <div className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
              frequency === 'light' ? 'border-primary bg-primary-light/30' : 'border-border hover:bg-muted/50'
            }`}>
              <RadioGroupItem value="light" id="light" />
              <div>
                <Label htmlFor="light" className="font-medium cursor-pointer">Light</Label>
                <p className="text-sm text-muted-foreground">1-2 reminders per day</p>
              </div>
            </div>
            
            <div className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
              frequency === 'standard' ? 'border-primary bg-primary-light/30' : 'border-border hover:bg-muted/50'
            }`}>
              <RadioGroupItem value="standard" id="standard" />
              <div>
                <Label htmlFor="standard" className="font-medium cursor-pointer">Standard</Label>
                <p className="text-sm text-muted-foreground">3-4 reminders per day (Recommended)</p>
              </div>
            </div>
            
            <div className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
              frequency === 'high' ? 'border-primary bg-primary-light/30' : 'border-border hover:bg-muted/50'
            }`}>
              <RadioGroupItem value="high" id="high" />
              <div>
                <Label htmlFor="high" className="font-medium cursor-pointer">High</Label>
                <p className="text-sm text-muted-foreground">5+ reminders per day for maximum accountability</p>
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>
      
      {/* Tone */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Volume2 className="w-4 h-4" />
            Message Tone
          </CardTitle>
          <CardDescription>Choose the style of reminder messages</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={tone} onValueChange={setTone} className="space-y-3">
            <div className={`p-4 rounded-lg border cursor-pointer transition-colors ${
              tone === 'gentle' ? 'border-primary bg-primary-light/30' : 'border-border hover:bg-muted/50'
            }`}>
              <div className="flex items-center gap-4 mb-2">
                <RadioGroupItem value="gentle" id="gentle" />
                <Label htmlFor="gentle" className="font-medium cursor-pointer">Gentle</Label>
              </div>
              <p className="text-sm text-muted-foreground ml-8 italic">
                "When you're ready, your exercises are waiting for you. Take your time! 😊"
              </p>
            </div>
            
            <div className={`p-4 rounded-lg border cursor-pointer transition-colors ${
              tone === 'motivational' ? 'border-primary bg-primary-light/30' : 'border-border hover:bg-muted/50'
            }`}>
              <div className="flex items-center gap-4 mb-2">
                <RadioGroupItem value="motivational" id="motivational" />
                <Label htmlFor="motivational" className="font-medium cursor-pointer">Motivational</Label>
              </div>
              <p className="text-sm text-muted-foreground ml-8 italic">
                "You're doing amazing! Let's crush today's exercises and get closer to your goals! 💪"
              </p>
            </div>
            
            <div className={`p-4 rounded-lg border cursor-pointer transition-colors ${
              tone === 'direct' ? 'border-primary bg-primary-light/30' : 'border-border hover:bg-muted/50'
            }`}>
              <div className="flex items-center gap-4 mb-2">
                <RadioGroupItem value="direct" id="direct" />
                <Label htmlFor="direct" className="font-medium cursor-pointer">Direct</Label>
              </div>
              <p className="text-sm text-muted-foreground ml-8 italic">
                "Time for your exercises. Complete: Knee Flexion (10 min), Quad Strengthening (15 min)"
              </p>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>
      
      {/* Language */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Language Preference</CardTitle>
          <CardDescription>Receive reminders in your preferred language</CardDescription>
        </CardHeader>
        <CardContent>
          <Select defaultValue="en">
            <SelectTrigger className="w-full sm:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="bm">Bahasa Malaysia</SelectItem>
              <SelectItem value="zh">Chinese (中文)</SelectItem>
              <SelectItem value="ta">Tamil (தமிழ்)</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
    </div>
  );
}
